import { useCurrentStateAndParams } from "@uirouter/react";

import { CardGroup } from "@similarweb/pro-logic-components";

import { useBrandOverviewData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/hooks/useBrandOverviewData";
import {
    getBaseWidgetRequestParams,
    getTopicsFilterParam,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

import { AveragePositionMetricCard } from "./cards/AveragePositionMetricCard";
import { MentionShareMetricCard } from "./cards/MentionShareMetricCard";
import { VisibilityMetricCard } from "./cards/VisibilityMetricCard";
import { getConfiguredCompetitorsCount, getMentionShareMetric, getVisibilityMetric } from "./utils";

const COMPACT_CARD_GROUP_HEIGHT = "120px";
const EXPANDED_CARD_GROUP_HEIGHT = "138px";

export const BrandOverviewComponent = ({
    campaign,
    campaignConfig,
    reportStatus,
    reportDuration,
}: ILLMReportWidget) => {
    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();

    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;

    const {
        data,
        isLoading: isLoadingData,
        isError,
    } = useBrandOverviewData(
        {
            ...getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
            topics: getTopicsFilterParam(queryTopic),
        },
        { enabled: canLoadData },
    );

    const hasData = !!data && !isError && reportStatus !== ReportStatus.Error;
    const isLoading = (isLoadingData || !campaign) && reportStatus !== ReportStatus.Error;
    const competitorsCount = getConfiguredCompetitorsCount(campaign);
    const hasCompetitiveSet = competitorsCount > 0;
    const visibilityRank = data?.competitive?.visibilityRank;
    const outOfCompetitorsMentionShare = data?.competitive?.outOfCompetitorsMentionShare;

    return (
        <CardGroup
            data-automation="brand-overview-widget"
            withDividers
            $height={
                visibilityRank != null || outOfCompetitorsMentionShare != null || hasCompetitiveSet
                    ? EXPANDED_CARD_GROUP_HEIGHT
                    : COMPACT_CARD_GROUP_HEIGHT
            }
        >
            <VisibilityMetricCard
                metric={getVisibilityMetric(data)}
                visibilityRank={visibilityRank}
                hasData={hasData}
                isLoading={isLoading}
            />
            <MentionShareMetricCard
                metric={getMentionShareMetric(data)}
                outOfCompetitorsMentionShare={outOfCompetitorsMentionShare}
                hasData={hasData}
                isLoading={isLoading}
            />
            <AveragePositionMetricCard
                averagePosition={data?.averagePosition}
                mentionPositionRank={data?.competitive?.mentionPositionRank}
                competitorsCount={competitorsCount}
                primaryBrand={campaign?.brands?.[0]}
                brandAnswers={data?.brandVisibility?.total?.brandMentions}
                hasData={hasData}
                isLoading={isLoading}
            />
        </CardGroup>
    );
};
